import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import type { ListPlacement } from '../../game-list-geometry';
import {
  Slot,
  PinnedRow,
  DIALOG_BRIDGE,
  Bar,
  Input,
  Count,
  IconBtn,
  ClearBtn,
  Divider,
  Actions,
  FIELD_MIN_PX,
  FoldBtn,
  ActionsFold,
} from './GameListFilter.styles';

export type GameListFilterProps = {
  value: string;
  onChange: (value: string) => void;
  /** The current match, 0-based; -1 when there is none (the count then reads "–/total"). */
  current: number;
  /** How many games match, across the whole list. */
  total: number;
  onPrevious: () => void;
  onNext: () => void;
  /** Where the list is shown, which decides where the floating field pins. */
  placement?: ListPlacement;
  /**
   * Controls for the field's row, at its right end. With them the row is always pinned to the top. `compact`
   * is true when their labels do not fit beside the field: the actions then show just their icons (a label
   * becoming a tooltip), though one may choose to stay labelled. Past that they wrap, within the group and
   * then under the field.
   */
  actions?: (compact: boolean) => React.ReactNode;
  /** The field alone, where its caller draws it (the open list's heading, which stays put): it neither pins nor floats,
   *  and carries no actions. */
  inline?: boolean;
  /** With `actions`, on a phone (the user, 2026-10-21): the field is folded away to its magnifier at the row's left, the
   *  actions at its right; a press opens it across the row, the actions out of its way, and it folds away again once
   *  emptied by its clear button, or emptied and left. */
  collapsible?: boolean;
};

/** The gap between the field and the actions (the row's flex gap). */
const ROW_GAP_PX = 10;

/**
 * The live filter above a game list. Empty, it is a plain field; once something is typed it floats at the
 * top of the screen on its own panel, with the match count and previous / next steps. Enter and Shift+Enter
 * step too, and Escape (or the clear button) empties it, which puts it back in place. With `actions`, the
 * field and the actions share one row that stays pinned to the top.
 */
export const GameListFilter = ({
  value,
  onChange,
  current,
  total,
  onPrevious,
  onNext,
  placement = 'page',
  actions,
  inline = false,
  collapsible = false,
}: GameListFilterProps): React.ReactElement => {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const floating = value !== '';
  const rowRef = React.useRef<HTMLDivElement>(null);
  const actionsRef = React.useRef<HTMLDivElement>(null);
  const [compact, setCompact] = React.useState(false);
  // How wide the actions are with their labels, measured while they show them.
  const labelledWidth = React.useRef(0);
  const hasActions = Boolean(actions);
  // Folded away (a phone's row with actions) until opened; open while it holds a filter, and while it is in use.
  const [open, setOpen] = React.useState(false);
  const folds = collapsible && hasActions && !inline;
  const folded = folds && !open && value === '';
  const wasFolded = React.useRef(folded);
  // Set by the press on the folded field, read (and cleared) once it has unfolded.
  const openedByPress = React.useRef(false);
  React.useEffect(() => {
    const pressed = openedByPress.current;
    openedByPress.current = false;
    // Opened by a press: the field takes the focus, the phone's keyboard comes up; the page does not move for it. Not
    // when it unfolds by itself (`collapsible` going off as a window or a tablet crosses the phone's width).
    if (pressed && wasFolded.current && !folded) inputRef.current?.focus({ preventScroll: true });
    wasFolded.current = folded;
  }, [folded]);
  // Syncs `compact` with the row's width: the labels go when the field would be squeezed under its minimum,
  // and come back once there is room for them again (their measured width, so it holds for any language).
  React.useLayoutEffect(() => {
    const row = rowRef.current;
    const tools = actionsRef.current;
    if (!hasActions || !row || !tools || typeof ResizeObserver === 'undefined') return undefined;
    const fit = (): void => {
      // The room the field and the actions share: inside the padding of the row they sit on.
      const line = tools.parentElement ?? row;
      const style = getComputedStyle(line);
      const room = line.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      setCompact((was) => {
        if (!was) labelledWidth.current = tools.scrollWidth;
        return room < FIELD_MIN_PX + ROW_GAP_PX + labelledWidth.current;
      });
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(row);
    observer.observe(tools);
    return () => observer.disconnect();
    // Again when the actions fold in or out of the row (a phone turned): they are a new node in their new place.
  }, [hasActions, folds]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (total === 0) return;
      if (event.shiftKey) onPrevious();
      else onNext();
    } else if (event.key === 'Escape' && floating) {
      event.preventDefault();
      onChange('');
    }
  };

  const field = (
    <Bar $floating={floating} $fold={folds ? (folded ? 'folded' : 'open') : undefined}>
      <Icon name="search" size={16} />
      <Input
        ref={inputRef}
        $folded={folded}
        tabIndex={folded ? -1 : undefined}
        aria-hidden={folded || undefined}
        type="text"
        inputMode="search"
        enterKeyHint="search"
        autoComplete="off"
        spellCheck={false}
        aria-label={t('Filter games')}
        placeholder={t('Filter by name, ID or provider')}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => {
          // Emptied and left (the phone's keyboard put away): folded away again.
          if (folds && value === '') setOpen(false);
        }}
      />
      {floating ? (
        <>
          <Count aria-live="polite">{total === 0 ? '0/0' : `${current < 0 ? '–' : current + 1}/${total}`}</Count>
          <ClearBtn
            type="button"
            aria-label={t('Clear search text')}
            onClick={() => {
              onChange('');
              // Folding, emptied by its clear button; else the field keeps the focus to type again.
              if (folds) setOpen(false);
              else inputRef.current?.focus();
            }}
          >
            <Icon name="close" size={14} />
          </ClearBtn>
          <Divider aria-hidden="true" />
          <IconBtn type="button" aria-label={t('Previous match')} disabled={total === 0} onClick={onPrevious}>
            <Icon name="chevron-up" size={18} />
          </IconBtn>
          <IconBtn type="button" aria-label={t('Next match')} disabled={total === 0} onClick={onNext}>
            <Icon name="chevron-down" size={18} />
          </IconBtn>
        </>
      ) : null}
      {folded ? (
        <FoldBtn
          type="button"
          aria-label={t('Filter games')}
          onClick={() => {
            openedByPress.current = true;
            setOpen(true);
          }}
        />
      ) : null}
    </Bar>
  );
  const tools = actions ? <Actions ref={actionsRef}>{actions(compact)}</Actions> : null;

  if (inline) return field;

  if (!hasActions) {
    return (
      <Slot ref={rowRef} $floating={floating} $placement={placement}>
        {field}
      </Slot>
    );
  }

  // With actions the row is always pinned (PinnedRow); `stuck` is whether it is held at its pin right now, the list
  // scrolling under it.
  return (
    <PinnedRow
      ref={rowRef}
      placement={placement}
      bridge={placement === 'dialog' ? DIALOG_BRIDGE : undefined}
      $placement={placement}
    >
      {field}
      {folds ? <ActionsFold $away={!folded}>{tools}</ActionsFold> : tools}
    </PinnedRow>
  );
};

export default GameListFilter;
